/* eslint-disable @pm/architecture/no-brand-logic */
import { useQuery } from '@apollo/client';
import { Banner, TextLink } from '@pm/ui';
import { useMarkdownComponents } from '@pm/ui/src/hooks/use-markdown-components/use-markdown-components';
import { useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import invariant from 'tiny-invariant';
import { useEnvironment } from '../../hooks/env';
import { BRAND_NAME, BrandId } from '../../types';
import { ACTIVE_ANNOUNCEMENT_QUERY } from './Announcement.graphql';

type AnnouncementProps = {
  locale?: string;
};

const getBrandId = (brand: string): string | undefined => {
  switch (brand) {
    case BRAND_NAME.Freddie:
      return BrandId.Freddie;
    case BRAND_NAME.Frida:
      return BrandId.Frida;
    case BRAND_NAME.FreddieUSA:
      return BrandId.FreddieUSA;
    default:
      return undefined;
  }
};

export const Announcement = ({ locale }: AnnouncementProps = {}) => {
  const { brand, formattingContext } = useEnvironment();
  const brandId = getBrandId(brand);
  const isProvider = formattingContext === 'provider';
  const isPatient = formattingContext === 'patient';

  const { data, loading, refetch } = useQuery(ACTIVE_ANNOUNCEMENT_QUERY, {
    variables: {
      brandId,
      locale,
      isProvider,
      isPatient,
    },
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refetch();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refetch]);

  const { ul, ol } = useMarkdownComponents({ variant: 'default' });

  if (loading || !data?.activeAnnouncement) {
    return null;
  }

  const announcement = data.activeAnnouncement;

  const markdownContent =
    formattingContext === 'provider'
      ? announcement.providerInfo || ''
      : [
          announcement.title || '',
          announcement.description || '',
          announcement.callToAction || '',
        ]
          .filter(Boolean)
          .join('\n');

  return (
    <Banner
      intent="warning"
      content={
        <ReactMarkdown
          components={{
            p: ({ children }) => <>{children}</>,
            ul,
            ol,
            a: ({ href, children, ...props }) => {
              invariant(
                href,
                'Href is required for anchor tags in announcements',
              );
              return (
                <TextLink target="_blank" to={href} external {...props}>
                  {children}
                </TextLink>
              );
            },
          }}
        >
          {markdownContent}
        </ReactMarkdown>
      }
    />
  );
};
